// The US states an organisation can name, and the laws each brings. Display mirror of backend
// lib/usLaws (US_STATES, US_STATE_REGULATIONS), which is what decides Organization.regulations;
// usStatesSync.test.ts in the backend fails when the two differ. Regenerate rather than hand-edit.

export const US_STATES = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "District of Columbia",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
] as const;

/** The laws each state adds while the United States is an operating country. */
export const US_STATE_REGULATIONS: Record<string, string[]> = {
  "Alabama": ["Alabama Data Breach Notification Law", "Alabama Personal Data Protection Act (APDPA)"],
  "Alaska": ["Alaska Data Breach Notification Law"],
  "Arizona": ["Arizona Data Breach Notification Law"],
  "Arkansas": ["Arkansas Data Breach Notification Law"],
  "California": ["California Data Breach Notification Law", "CCPA / CPRA (California)", "California Confidentiality of Medical Information Act (CMIA)", "California Delete Act (SB 362)"],
  "Colorado": ["Colorado Data Breach Notification Law", "Colorado Privacy Act (CPA)", "Colorado Automated Decision-Making Technology Law (SB 26-189)"],
  "Connecticut": ["Connecticut Data Breach Notification Law", "Connecticut Data Privacy Act (CTDPA)", "Connecticut Cybersecurity Safe Harbor (Conn. Gen. Stat. §42-901)"],
  "Delaware": ["Delaware Data Breach Notification Law", "Delaware Personal Data Privacy Act (DPDPA)"],
  "District of Columbia": ["District of Columbia Data Breach Notification Law"],
  "Florida": ["Florida Data Breach Notification Law", "Florida Digital Bill of Rights (FDBR)"],
  "Georgia": ["Georgia Data Breach Notification Law"],
  "Hawaii": ["Hawaii Data Breach Notification Law"],
  "Idaho": ["Idaho Data Breach Notification Law"],
  "Illinois": ["Illinois Data Breach Notification Law", "Illinois Biometric Information Privacy Act (BIPA)", "Illinois Human Rights Act — Artificial Intelligence in Employment (HB 3773)"],
  "Indiana": ["Indiana Data Breach Notification Law", "Indiana Consumer Data Protection Act (INCDPA)"],
  "Iowa": ["Iowa Data Breach Notification Law", "Iowa Consumer Data Protection Act (ICDPA)", "Iowa Cybersecurity Affirmative Defense (Iowa Code ch. 554G)"],
  "Kansas": ["Kansas Data Breach Notification Law"],
  "Kentucky": ["Kentucky Data Breach Notification Law", "Kentucky Consumer Data Protection Act (KCDPA)"],
  "Louisiana": ["Louisiana Data Breach Notification Law", "Louisiana Data Privacy Act (LDPA)"],
  "Maine": ["Maine Data Breach Notification Law"],
  "Maryland": ["Maryland Data Breach Notification Law", "Maryland Online Data Privacy Act (MODPA)"],
  "Massachusetts": ["Massachusetts Data Breach Notification Law", "Massachusetts Standards for the Protection of Personal Information (201 CMR 17.00)"],
  "Michigan": ["Michigan Data Breach Notification Law"],
  "Minnesota": ["Minnesota Data Breach Notification Law", "Minnesota Consumer Data Privacy Act (MCDPA)"],
  "Mississippi": ["Mississippi Data Breach Notification Law"],
  "Missouri": ["Missouri Data Breach Notification Law"],
  "Montana": ["Montana Data Breach Notification Law", "Montana Consumer Data Privacy Act (MCDPA)"],
  "Nebraska": ["Nebraska Data Breach Notification Law", "Nebraska Data Privacy Act (NDPA)"],
  "Nevada": ["Nevada Data Breach Notification Law", "Nevada Consumer Health Data Privacy Law (SB 370)"],
  "New Hampshire": ["New Hampshire Data Breach Notification Law", "New Hampshire Privacy Act (NHPA)"],
  "New Jersey": ["New Jersey Data Breach Notification Law", "New Jersey Data Privacy Act (NJDPA)"],
  "New Mexico": ["New Mexico Data Breach Notification Law"],
  "New York": ["New York Data Breach Notification Law", "New York DFS Cybersecurity Regulation (23 NYCRR 500)", "New York Child Data Protection Act (NYCDPA)"],
  "North Carolina": ["North Carolina Data Breach Notification Law"],
  "North Dakota": ["North Dakota Data Breach Notification Law"],
  "Ohio": ["Ohio Data Breach Notification Law", "Ohio Data Protection Act (R.C. Chapter 1354)"],
  "Oklahoma": ["Oklahoma Data Breach Notification Law", "Oklahoma Consumer Data Privacy Act (OCDPA)"],
  "Oregon": ["Oregon Data Breach Notification Law", "Oregon Consumer Privacy Act (OCPA)"],
  "Pennsylvania": ["Pennsylvania Data Breach Notification Law"],
  "Rhode Island": ["Rhode Island Data Breach Notification Law", "Rhode Island Data Transparency and Privacy Protection Act (RIDTPPA)"],
  "South Carolina": ["South Carolina Data Breach Notification Law"],
  "South Dakota": ["South Dakota Data Breach Notification Law"],
  "Tennessee": ["Tennessee Data Breach Notification Law", "Tennessee Information Protection Act (TIPA)"],
  "Texas": ["Texas Data Breach Notification Law", "Texas Data Privacy and Security Act (TDPSA)", "Texas Data Broker Law (Bus. & Com. Code Ch. 509)", "Texas Capture or Use of Biometric Identifier Act (CUBI)", "Texas Responsible Artificial Intelligence Governance Act (TRAIGA)"],
  "Utah": ["Utah Data Breach Notification Law", "Utah Consumer Privacy Act (UCPA)", "Utah Cybersecurity Affirmative Defense Act", "Utah Artificial Intelligence Policy Act (AIPA)"],
  "Vermont": ["Vermont Data Breach Notification Law", "Vermont Data Privacy and Online Surveillance Act (VDPOSA)", "Vermont Data Broker Law (9 V.S.A. §§2430, 2446-2447)"],
  "Virginia": ["Virginia Data Breach Notification Law", "Virginia Consumer Data Protection Act (VCDPA)"],
  "Washington": ["Washington Data Breach Notification Law", "Washington Biometric Identifiers Law (RCW 19.375)", "Washington My Health My Data Act"],
  "West Virginia": ["West Virginia Data Breach Notification Law"],
  "Wisconsin": ["Wisconsin Data Breach Notification Law"],
  "Wyoming": ["Wyoming Data Breach Notification Law"],
};

/** The Legal page's category for each US law the federal list does not already classify. */
export const US_LAW_CATEGORY: Record<string, string> = {
  "Alabama Data Breach Notification Law": "Breach Notification",
  "Alaska Data Breach Notification Law": "Breach Notification",
  "Arizona Data Breach Notification Law": "Breach Notification",
  "Arkansas Data Breach Notification Law": "Breach Notification",
  "California Data Breach Notification Law": "Breach Notification",
  "Colorado Data Breach Notification Law": "Breach Notification",
  "Connecticut Data Breach Notification Law": "Breach Notification",
  "Delaware Data Breach Notification Law": "Breach Notification",
  "District of Columbia Data Breach Notification Law": "Breach Notification",
  "Florida Data Breach Notification Law": "Breach Notification",
  "Georgia Data Breach Notification Law": "Breach Notification",
  "Hawaii Data Breach Notification Law": "Breach Notification",
  "Idaho Data Breach Notification Law": "Breach Notification",
  "Illinois Data Breach Notification Law": "Breach Notification",
  "Indiana Data Breach Notification Law": "Breach Notification",
  "Iowa Data Breach Notification Law": "Breach Notification",
  "Kansas Data Breach Notification Law": "Breach Notification",
  "Kentucky Data Breach Notification Law": "Breach Notification",
  "Louisiana Data Breach Notification Law": "Breach Notification",
  "Maine Data Breach Notification Law": "Breach Notification",
  "Maryland Data Breach Notification Law": "Breach Notification",
  "Massachusetts Data Breach Notification Law": "Breach Notification",
  "Michigan Data Breach Notification Law": "Breach Notification",
  "Minnesota Data Breach Notification Law": "Breach Notification",
  "Mississippi Data Breach Notification Law": "Breach Notification",
  "Missouri Data Breach Notification Law": "Breach Notification",
  "Montana Data Breach Notification Law": "Breach Notification",
  "Nebraska Data Breach Notification Law": "Breach Notification",
  "Nevada Data Breach Notification Law": "Breach Notification",
  "New Hampshire Data Breach Notification Law": "Breach Notification",
  "New Jersey Data Breach Notification Law": "Breach Notification",
  "New Mexico Data Breach Notification Law": "Breach Notification",
  "New York Data Breach Notification Law": "Breach Notification",
  "North Carolina Data Breach Notification Law": "Breach Notification",
  "North Dakota Data Breach Notification Law": "Breach Notification",
  "Ohio Data Breach Notification Law": "Breach Notification",
  "Oklahoma Data Breach Notification Law": "Breach Notification",
  "Oregon Data Breach Notification Law": "Breach Notification",
  "Pennsylvania Data Breach Notification Law": "Breach Notification",
  "Rhode Island Data Breach Notification Law": "Breach Notification",
  "South Carolina Data Breach Notification Law": "Breach Notification",
  "South Dakota Data Breach Notification Law": "Breach Notification",
  "Tennessee Data Breach Notification Law": "Breach Notification",
  "Texas Data Breach Notification Law": "Breach Notification",
  "Utah Data Breach Notification Law": "Breach Notification",
  "Vermont Data Breach Notification Law": "Breach Notification",
  "Virginia Data Breach Notification Law": "Breach Notification",
  "Washington Data Breach Notification Law": "Breach Notification",
  "West Virginia Data Breach Notification Law": "Breach Notification",
  "Wisconsin Data Breach Notification Law": "Breach Notification",
  "Wyoming Data Breach Notification Law": "Breach Notification",
  "CCPA / CPRA (California)": "Data Protection",
  "Virginia Consumer Data Protection Act (VCDPA)": "Data Protection",
  "Colorado Privacy Act (CPA)": "Data Protection",
  "Connecticut Data Privacy Act (CTDPA)": "Data Protection",
  "Utah Consumer Privacy Act (UCPA)": "Data Protection",
  "Iowa Consumer Data Protection Act (ICDPA)": "Data Protection",
  "Indiana Consumer Data Protection Act (INCDPA)": "Data Protection",
  "Tennessee Information Protection Act (TIPA)": "Data Protection",
  "Montana Consumer Data Privacy Act (MCDPA)": "Data Protection",
  "Oregon Consumer Privacy Act (OCPA)": "Data Protection",
  "Texas Data Privacy and Security Act (TDPSA)": "Data Protection",
  "Florida Digital Bill of Rights (FDBR)": "Data Protection",
  "Delaware Personal Data Privacy Act (DPDPA)": "Data Protection",
  "New Hampshire Privacy Act (NHPA)": "Data Protection",
  "New Jersey Data Privacy Act (NJDPA)": "Data Protection",
  "Kentucky Consumer Data Protection Act (KCDPA)": "Data Protection",
  "Nebraska Data Privacy Act (NDPA)": "Data Protection",
  "Maryland Online Data Privacy Act (MODPA)": "Data Protection",
  "Minnesota Consumer Data Privacy Act (MCDPA)": "Data Protection",
  "Rhode Island Data Transparency and Privacy Protection Act (RIDTPPA)": "Data Protection",
  "Oklahoma Consumer Data Privacy Act (OCDPA)": "Data Protection",
  "Alabama Personal Data Protection Act (APDPA)": "Data Protection",
  "Louisiana Data Privacy Act (LDPA)": "Data Protection",
  "Vermont Data Privacy and Online Surveillance Act (VDPOSA)": "Data Protection",
  "Massachusetts Standards for the Protection of Personal Information (201 CMR 17.00)": "Cybersecurity",
  "New York DFS Cybersecurity Regulation (23 NYCRR 500)": "Cybersecurity",
  "Ohio Data Protection Act (R.C. Chapter 1354)": "Cybersecurity",
  "Utah Cybersecurity Affirmative Defense Act": "Cybersecurity",
  "Connecticut Cybersecurity Safe Harbor (Conn. Gen. Stat. §42-901)": "Cybersecurity",
  "Iowa Cybersecurity Affirmative Defense (Iowa Code ch. 554G)": "Cybersecurity",
  "California Confidentiality of Medical Information Act (CMIA)": "Data Protection",
  "California Delete Act (SB 362)": "Data Protection",
  "Vermont Data Broker Law (9 V.S.A. §§2430, 2446-2447)": "Data Protection",
  "New York Child Data Protection Act (NYCDPA)": "Online Services",
  "Texas Data Broker Law (Bus. & Com. Code Ch. 509)": "Data Protection",
  "Illinois Biometric Information Privacy Act (BIPA)": "Data Protection",
  "Texas Capture or Use of Biometric Identifier Act (CUBI)": "Data Protection",
  "Washington Biometric Identifiers Law (RCW 19.375)": "Data Protection",
  "Washington My Health My Data Act": "Data Protection",
  "Nevada Consumer Health Data Privacy Law (SB 370)": "Data Protection",
  "Colorado Automated Decision-Making Technology Law (SB 26-189)": "AI",
  "Utah Artificial Intelligence Policy Act (AIPA)": "AI",
  "Texas Responsible Artificial Intelligence Governance Act (TRAIGA)": "AI",
  "Illinois Human Rights Act — Artificial Intelligence in Employment (HB 3773)": "AI",
  "Children's Online Privacy Protection Act (COPPA)": "Online Services",
  "Family Educational Rights and Privacy Act (FERPA)": "Data Protection",
  "Fair Credit Reporting Act (FCRA)": "Financial Privacy",
  "CAN-SPAM Act": "Online Services",
  "Telephone Consumer Protection Act (TCPA)": "Data Protection",
  "Electronic Communications Privacy Act (ECPA)": "Data Protection",
  "SEC Cybersecurity Disclosure Rules (2023)": "Corporate Records",
  "FTC Health Breach Notification Rule": "Data Protection",
  "Cyber Incident Reporting for Critical Infrastructure Act 2022 (CIRCIA)": "Cybersecurity",
  "DFARS 252.204-7012 and CMMC": "Cybersecurity",
  "FCC CPNI and Data Breach Rules": "Data Protection",
};

/** The state a law belongs to. */
export const US_STATE_OF_LAW: Record<string, string> = Object.fromEntries(
  Object.entries(US_STATE_REGULATIONS).flatMap(([state, laws]) => laws.map((law) => [law, state])),
);

/** The laws of the named states, de-duplicated. */
export function regulationsForUsStates(states: readonly string[]): string[] {
  return Array.from(new Set(states.flatMap((s) => US_STATE_REGULATIONS[s] ?? [])));
}
